import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import FitifyLogo from "assets/images/nFitifyLogo.svg";

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavClick = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };

  const handleSectionClick = (sectionId) => {
    setMobileMenuOpen(false);

    if (location.pathname !== "/") {
      sessionStorage.setItem("isSectionNavigation", "true");
      navigate("/");
      // Wait for navigation then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 500);
    } else {
      // Already on home page, just scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    }
  };

  const navItems = [
    { name: "Home", path: "/", type: "route" },
    { name: "About Us", path: "/about", type: "route" },
    { name: "Features", path: "/features", type: "route" },
    { name: "Testimonials", path: "testimonials", type: "section" },
    { name: "Pricing", path: "pricing", type: "section" },
    { name: "Contact Us", path: "contact", type: "section" },
  ];

  const renderNavItem = (item) => {
    if (item.type === "section") {
      return (
        <Typography
          key={item.name}
          onClick={() => handleSectionClick(item.path)}
          sx={{
            color: "#FFFFFF",
            fontFamily: "Outfit",
            fontWeight: 400,
            fontSize: { xs: "18px", lg: "16px" },
            cursor: "pointer",
            py: { xs: 1, lg: 0 },
            px: { xs: 2, lg: 1 },
            borderRadius: "8px",
            transition: "all 0.3s ease",
            "&:hover": {
              color: "#17c1e8",
              backgroundColor: {
                xs: "rgba(255, 255, 255, 0.1)",
                lg: "transparent",
              },
            },
          }}
        >
          {item.name}
        </Typography>
      );
    }

    return (
      <Typography
        key={item.name}
        component={Link}
        to={item.path}
        sx={{
          color: "#FFFFFF",
          textDecoration: "none",
          fontFamily: "Outfit",
          fontWeight: 400,
          fontSize: { xs: "18px", lg: "16px" },
          py: { xs: 1, lg: 0 },
          px: { xs: 2, lg: 1 },
          borderRadius: "8px",
          transition: "all 0.3s ease",
          "&:hover": {
            color: "#17c1e8",
            backgroundColor: {
              xs: "rgba(255, 255, 255, 0.1)",
              lg: "transparent",
            },
          },
        }}
      >
        {item.name}
      </Typography>
    );
  };

  return (
    <>
      {/* Main Navbar */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: "rgba(26, 26, 26, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 2,
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => navigate("/")}
            >
              <Box
                component="img"
                src={FitifyLogo}
                alt="Fitify Logo"
                sx={{
                  height: "40px",
                  width: "auto",
                  maxWidth: "120px",
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
                gap: 2,
              }}
            >
              {navItems.map(renderNavItem)}

              <Button
                component={Link}
                to="/login"
                sx={{
                  color: "#5BF2FF",
                  px: 2,
                  py: 1,
                  borderRadius: "8px",
                  background: "rgba(255, 255, 255, 0.15)",
                  textTransform: "none",
                  fontSize: "16px",
                  fontWeight: 500,
                  fontFamily: "Outfit",
                  "&:hover": {
                    background: "rgba(255, 255, 255, 0.25)",
                  },
                }}
              >
                Login
              </Button>
            </Box>

            {/* Mobile Menu Button */}
            <IconButton
              onClick={toggleMobileMenu}
              sx={{
                display: { xs: "block", lg: "none" },
                color: "#FFFFFF",
              }}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={toggleMobileMenu}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: "100%",
            maxWidth: "400px",
            background: "rgba(26, 26, 26, 0.98)",
            backdropFilter: "blur(15px)",
            borderLeft: "1px solid rgba(255, 255, 255, 0.1)",
            borderRadius: 0,
            margin: 0,
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
            py: 0,
            px: 0,
          }}
        >
          {/* Close Button */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              p: 2,
            }}
          >
            <IconButton
              onClick={toggleMobileMenu}
              sx={{
                color: "#FFFFFF",
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Mobile Navigation Items */}
          <Box sx={{ flex: 1, px: 3 }}>
            {navItems.map((item) => (
              <Box
                key={item.name}
                onClick={() => {
                  if (item.type === "section") {
                    handleSectionClick(item.path);
                  } else {
                    handleNavClick(item.path);
                  }
                }}
                sx={{
                  py: 2,
                  px: 2,
                  mb: 1,
                  borderRadius: "12px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                    transform: "translateX(8px)",
                  },
                }}
              >
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontFamily: "Outfit",
                    fontWeight: 400,
                    fontSize: "20px",
                    textAlign: "center",
                  }}
                >
                  {item.name}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Mobile Login Button */}
          <Box sx={{ mt: 4, px: 3, pb: 3 }}>
            <Button
              component={Link}
              to="/login"
              onClick={() => setMobileMenuOpen(false)}
              fullWidth
              sx={{
                color: "#5BF2FF",
                py: 2,
                borderRadius: "12px",
                background: "rgba(255, 255, 255, 0.15)",
                textTransform: "none",
                fontSize: "18px",
                fontWeight: 500,
                fontFamily: "Outfit",
                border: "2px solid rgba(255, 255, 255, 0.3)",
                "&:hover": {
                  background: "rgba(255, 255, 255, 0.25)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Login
            </Button>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar;
